import React, { useRef, useState } from 'react';
import { Card, CardContent, TextField, Button, Box, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNotify, useRedirect } from 'react-admin';
import { usersProxy } from './apiGateway';

export const UsersSearchPage: React.FC = () => {
    const [nickname, setNickname] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [bsonId, setBsonId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const phoneInputRef = useRef<HTMLInputElement | null>(null);
    const notify = useNotify();
    const redirect = useRedirect();

    const formatRuPhone = (value: string) => {
        const digits = value.replace(/\D/g, '');
        if (!digits) {
            return { formatted: '', normalized: '', addedPrefix: false };
        }

        let normalized = digits;
        let addedPrefix = false;
        if (normalized.startsWith('8')) {
            normalized = `7${normalized.slice(1)}`;
        }
        if (!normalized.startsWith('7')) {
            normalized = `7${normalized}`;
            addedPrefix = true;
        }
        normalized = normalized.slice(0, 11);

        const part1 = normalized.slice(1, 4);
        const part2 = normalized.slice(4, 7);
        const part3 = normalized.slice(7, 9);
        const part4 = normalized.slice(9, 11);

        let result = `+${normalized[0]}`;
        if (part1) {
            result += ` (${part1}`;
            if (part1.length === 3) {
                result += ')';
            }
        }
        if (part2) {
            result += ` ${part2}`;
        }
        if (part3) {
            result += `-${part3}`;
        }
        if (part4) {
            result += `-${part4}`;
        }

        return { formatted: result, normalized, addedPrefix };
    };

    const getCaretPosition = (formatted: string, digitsBeforeCaret: number) => {
        if (digitsBeforeCaret <= 0) {
            const openParenIndex = formatted.indexOf('(');
            if (openParenIndex >= 0) {
                return openParenIndex + 1;
            }
            return formatted.length;
        }

        let seenDigits = 0;
        for (let index = 0; index < formatted.length; index += 1) {
            if (/\d/.test(formatted[index])) {
                seenDigits += 1;
                if (seenDigits === digitsBeforeCaret) {
                    return index + 1;
                }
            }
        }

        return formatted.length;
    };

    const hasAnyValue = Boolean(nickname.trim() || phoneNumber.trim() || bsonId.trim());

    const normalizePhoneNumber = (value: string) => value.replace(/[()\s-]/g, '');

    const resolveApiErrorStatus = (err: unknown) => {
        if (typeof err === 'object' && err && 'status' in err) {
            const status = (err as { status?: unknown }).status;
            if (typeof status === 'number') {
                return status;
            }
        }

        if (err instanceof Error) {
            const match = err.message.match(/API Error: (\d+)/);
            if (match) {
                return Number(match[1]);
            }
        }

        return null;
    };

    const handleSearch = async () => {
        if (!hasAnyValue) {
            notify('Введите nickname, номер телефона или BSON ID.', { type: 'warning' });
            return;
        }

        const trimmedNickname = nickname.trim();
        const trimmedPhone = phoneNumber.trim();
        const trimmedId = bsonId.trim();

        const params: { nickname?: string; phone?: string; id?: string } = {};
        if (trimmedId) {
            params.id = trimmedId;
        } else if (trimmedNickname) {
            params.nickname = trimmedNickname;
        } else if (trimmedPhone) {
            params.phone = normalizePhoneNumber(trimmedPhone);
        }

        setIsLoading(true);

        try {
            const user = await usersProxy.search(params);
            if (!user?.id) {
                throw new Error('User not found');
            }
            redirect('show', 'users', user.id);
        } catch (err) {
            const status = resolveApiErrorStatus(err);
            if (status === 404) {
                notify('Пользователь по заданным параметрам не найден', { type: 'warning' });
            } else {
                notify('Ошибка при поиске пользователя', { type: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;
        const selectionStart = event.target.selectionStart ?? rawValue.length;
        const digitsBeforeCaret = rawValue.slice(0, selectionStart).replace(/\D/g, '').length;
        const { formatted, normalized, addedPrefix } = formatRuPhone(rawValue);

        let adjustedDigitsBeforeCaret = digitsBeforeCaret;
        if (addedPrefix && adjustedDigitsBeforeCaret > 0) {
            adjustedDigitsBeforeCaret += 1;
        }

        setPhoneNumber(formatted);

        if (!formatted) {
            return;
        }

        const clampedDigits = Math.min(adjustedDigitsBeforeCaret, normalized.length);
        const nextCaret = getCaretPosition(formatted, clampedDigits);

        requestAnimationFrame(() => {
            if (phoneInputRef.current) {
                phoneInputRef.current.setSelectionRange(nextCaret, nextCaret);
            }
        });
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200 }}>
            <Card>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Поиск пользователей
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Можно искать по nickname, номеру телефона или BSON ID.
                    </Typography>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Никнейм"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="kirill_baunti"
                            variant="outlined"
                        />
                        <TextField
                            fullWidth
                            label="Номер телефона"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            onKeyPress={handleKeyPress}
                            placeholder="+7 (999) 123-45-67"
                            variant="outlined"
                            type="tel"
                            inputRef={phoneInputRef}
                        />
                        <TextField
                            fullWidth
                            label="ID"
                            value={bsonId}
                            onChange={(e) => setBsonId(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="686934dff01db5688b548791"
                            variant="outlined"
                        />

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                disabled={!hasAnyValue || isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{ minWidth: 120, height: 36 }}
                            >
                                {isLoading ? 'Поиск...' : 'Найти'}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};
